import React, { useState } from 'react';
import useSWR from 'swr';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Button,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  LinearProgress,
  Fab,
  Divider,
  Paper,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Edit as EditIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  Language as LanguageIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { fetcher, axiosService, useUser } from 'shared/helpers/axios';
import PageWrapper from 'components/common/layout/PageWrapper';

const OrganizationCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
    transform: 'translateY(-2px)'
  }
}));

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
  }
}));

const OrganizationsPage = () => {
  const { refreshUserData } = useUser();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    // Organization selection/creation
    selectedOrganizationId: '',
    createNewOrganization: false,
    // Organization fields (when creating new)
    name: '',
    short_name: '',
    org_type: 'other',
    website: '',
    // Department selection/creation
    selectedDepartmentId: '',
    createNewDepartment: false,
    departmentName: '',
    // Membership fields
    role: 'researcher',
    start_date: new Date().toISOString().split('T')[0], // Today's date
    end_date: '',
    status: 'active'
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  // SWR hooks for data fetching
  const { data: organizations = [], isLoading: orgsLoading, mutate: mutateOrganizations } = useSWR(
    '/consortium/organizations/my-organizations/',
    fetcher 
  );

  const { data: allOrganizations = [], mutate: mutateAllOrganizations } = useSWR(
    '/consortium/organizations/',
    fetcher
  );

  const { data: departmentsData } = useSWR(
    (formData.selectedOrganizationId && !formData.createNewOrganization) ? `/consortium/organizations/${formData.selectedOrganizationId}/departments/` : null,
    fetcher
  );
  
  // Handle both paginated and non-paginated responses
  const departments = departmentsData?.results || departmentsData || [];

  const handleCreateOrganization = async () => {
    // Validate form
    const errors = {};
    if (!formData.createNewOrganization && !formData.selectedOrganizationId) {
      errors.organization = 'Please select an organization or choose to create a new one';
    }
    if (formData.createNewOrganization && !formData.name.trim()) {
      errors.name = 'Organization name is required';
    }
    if (formData.createNewOrganization && !formData.short_name.trim()) {
      errors.short_name = 'Organization short name is required';
    }
    if (!formData.role) errors.role = 'Role is required';
    if (!formData.start_date) errors.start_date = 'Start date is required';

    // Validate department if provided
    if (formData.departmentName && !formData.createNewDepartment && !formData.selectedDepartmentId) {
      errors.department = 'Please select an existing department or choose to create a new one';
    }
    if (formData.createNewDepartment && !formData.departmentName.trim()) {
      errors.departmentName = 'Department name is required when creating a new department';
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitLoading(true);
    try {
      let organizationId;
      let departmentId = null;

      // Step 1: Get or create organization
      if (formData.createNewOrganization) {
        // Create new organization
        const orgResponse = await axiosService.post('/consortium/organizations/', {
          name: formData.name,
          short_name: formData.short_name,
          org_type: formData.org_type,
          website: formData.website || null
        });
        organizationId = orgResponse.data.id;
        // Update all organizations cache
        mutateAllOrganizations((current) => {
          if (current && current.results) {
            return {
              ...current,
              results: [...current.results, orgResponse.data]
            };
          }
          return current;
        }, false);
      } else {
        // Use selected existing organization
        organizationId = formData.selectedOrganizationId;
      }

      // Step 2: Handle department
      if (formData.departmentName) {
        if (formData.createNewDepartment) {
          // Create new department using nested route
          const deptResponse = await fetcher.post(`/consortium/organizations/${organizationId}/departments/`, {
            name: formData.departmentName
          });
          departmentId = deptResponse.data.id;
        } else if (formData.selectedDepartmentId) {
          // Use existing department
          departmentId = formData.selectedDepartmentId;
        }
      }

      // Step 3: Create membership
      await fetcher.post('/consortium/organization-memberships/', {
        org: organizationId,
        role: formData.role,
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        status: formData.status,
        department: departmentId
      });

      // Step 4: Refresh organizations list and user data (so org_memberships is up to date)
      mutateOrganizations();
      refreshUserData();

      // Reset form
      handleCloseDialog();
      setCreateDialogOpen(false);

    } catch (error) {
      console.error('Error creating organization/membership:', error);
      if (error.response && error.response.data) {
        setFormErrors(error.response.data.errors || { general: 'Failed to create organization membership' });
      } else {
        setFormErrors({ general: 'Network error occurred' });
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleOrganizationSelectionChange = (value) => {
    const isCreateNew = value === 'create-new';
    setFormData(prev => ({
      ...prev,
      selectedOrganizationId: isCreateNew ? '' : value,
      createNewOrganization: isCreateNew,
      // Reset department when organization changes
      selectedDepartmentId: '',
      createNewDepartment: false,
      departmentName: ''
    }));
  };

  const handleDepartmentSelectionChange = (value) => {
    const isCreateNew = value === 'create-new';
    setFormData(prev => ({
      ...prev,
      selectedDepartmentId: isCreateNew ? '' : value,
      createNewDepartment: isCreateNew
    }));
  };

  const handleCloseDialog = () => {
    setCreateDialogOpen(false);
    setFormData({
      selectedOrganizationId: '',
      createNewOrganization: false,
      name: '',
      short_name: '',
      org_type: 'other',
      website: '',
      selectedDepartmentId: '',
      createNewDepartment: false,
      departmentName: '',
      role: 'researcher',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      status: 'active'
    });
    setFormErrors({});
  };

  const getRoleColor = (role) => {
    const colors = {
      director: 'error',
      manager: 'warning',
      faculty: 'info',
      researcher: 'success',
      staff: 'default',
      student: 'secondary',
      other: 'default'
    };
    return colors[role] || 'default';
  };

  const getOrgTypeIcon = (type) => {
    switch (type) {
      case 'university':
        return <BusinessIcon />;
      case 'research_institute':
        return <WorkIcon />;
      case 'company':
        return <BusinessIcon />;
      case 'government':
        return <BusinessIcon />;
      case 'non_profit':
        return <PeopleIcon />;
      default:
        return <BusinessIcon />;
    }
  };

  if (orgsLoading) {
    return (
      <PageWrapper>
        <Container maxWidth="lg">
          <Box sx={{ mt: 4, mb: 4 }}>
            <LinearProgress />
            <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
              Loading your organizations...
            </Typography>
          </Box>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              My Organizations
            </Typography>
            <Fab
              color="primary"
              aria-label="add organization"
              onClick={() => setCreateDialogOpen(true)}
              sx={{ boxShadow: 3 }}
            >
              <AddIcon />
            </Fab>
          </Box>

          {/* Stats */}
          {organizations.length > 0 && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {organizations.length}
                  </Typography>
                  <Typography variant="body2">
                    Organizations
                  </Typography>
                </StatCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {organizations.filter(org => org.membership?.status === 'active').length}
                  </Typography>
                  <Typography variant="body2">
                    Active Memberships
                  </Typography>
                </StatCard>
              </Grid>
            </Grid>
          )}

          {/* Organizations List */}
          {organizations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                No organizations yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Create your first organization to get started with collaborations and funding opportunities.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                size="large"
              >
                Create Organization
              </Button>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {organizations.map((org) => (
                <Grid item xs={12} md={6} lg={4} key={org.id}>
                  <OrganizationCard>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                          {getOrgTypeIcon(org.org_type)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {org.name}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {org.short_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {org.org_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </Typography>
                        </Box>
                        {org.is_verified && (
                          <CheckCircleIcon sx={{ color: 'success.main' }} />
                        )}
                      </Box>

                      {org.membership && (
                        <Box sx={{ mb: 2 }}>
                          <Chip
                            label={org.membership.role.replace(/\b\w/g, l => l.toUpperCase())}
                            color={getRoleColor(org.membership.role)}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <Chip
                            label={org.membership.status}
                            color={org.membership.status === 'active' ? 'success' : 'default'}
                            size="small"
                          />
                        </Box>
                      )}

                      {org.website && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <LanguageIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="primary">
                            {org.website}
                          </Typography>
                        </Box>
                      )}

                      {org.membership && (
                        <Typography variant="body2" color="text.secondary">
                          Member since {new Date(org.membership.start_date).toLocaleDateString()}
                        </Typography>
                      )}
                    </CardContent>
                    <CardActions>
                      <Button size="small" startIcon={<EditIcon />}>
                        Edit
                      </Button>
                      <Button size="small" startIcon={<PeopleIcon />}>
                        View Members
                      </Button>
                    </CardActions>
                  </OrganizationCard>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Create Organization Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BusinessIcon sx={{ mr: 1 }} />
              Create New Organization
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              {formErrors.general && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {formErrors.general}
                </Alert>
              )}

              <Typography variant="h6" sx={{ mb: 2 }}>
                Organization
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!formErrors.organization}>
                    <InputLabel>Select Organization</InputLabel>
                    <Select
                      value={formData.createNewOrganization ? 'create-new' : formData.selectedOrganizationId}
                      label="Select Organization"
                      onChange={(e) => handleOrganizationSelectionChange(e.target.value)}
                    >
                      <MenuItem value="create-new">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AddIcon sx={{ mr: 1, fontSize: 18 }} />
                          Create New Organization
                        </Box>
                      </MenuItem>
                      {allOrganizations?.results.map((org) => (
                        <MenuItem key={org.id} value={org.id}>
                          {org.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.organization && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                        {formErrors.organization}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {formData.createNewOrganization && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Organization Name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        error={!!formErrors.name}
                        helperText={formErrors.name}
                        required
                      />
                    </Grid>



                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Organization Name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        error={!!formErrors.name}
                        helperText={formErrors.name}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Organization Type</InputLabel>
                        <Select
                          value={formData.org_type}
                          label="Organization Type"
                          onChange={(e) => handleInputChange('org_type', e.target.value)}
                        >
                          <MenuItem value="university">University</MenuItem>
                          <MenuItem value="research_institute">Research Institute</MenuItem>
                          <MenuItem value="company">Company</MenuItem>
                          <MenuItem value="government">Government Agency</MenuItem>
                          <MenuItem value="non_profit">Non-Profit Organization</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Website"
                        value={formData.website}
                        onChange={(e) => handleInputChange('website', e.target.value)}
                        placeholder="https://example.com"
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ mb: 2 }}>
                Department (Optional)
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!formErrors.department}>
                    <InputLabel>Select Department</InputLabel>
                    <Select
                      value={formData.createNewDepartment ? 'create-new' : formData.selectedDepartmentId}
                      label="Select Department"
                      onChange={(e) => handleDepartmentSelectionChange(e.target.value)}
                      disabled={!formData.selectedOrganizationId && !formData.createNewOrganization}
                    >
                      <MenuItem value="">
                        <em>No department</em>
                      </MenuItem>
                      <MenuItem value="create-new">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AddIcon sx={{ mr: 1, fontSize: 18 }} />
                          Create New Department
                        </Box>
                      </MenuItem>
                      {departments?.map((dept) => (
                        <MenuItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.department && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                        {formErrors.department}
                      </Typography>
                    )}
                  </FormControl>
                  {(!formData.selectedOrganizationId && !formData.createNewOrganization) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Please select an organization first
                    </Typography>
                  )}
                </Grid>

                {formData.createNewDepartment && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="New Department Name"
                      value={formData.departmentName}
                      onChange={(e) => handleInputChange('departmentName', e.target.value)}
                      error={!!formErrors.departmentName}
                      helperText={formErrors.departmentName}
                      placeholder="e.g., Computer Science, Physics, etc."
                    />
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ mb: 2 }}>
                Your Membership Details
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Your Role</InputLabel>
                    <Select
                      value={formData.role}
                      label="Your Role"
                      onChange={(e) => handleInputChange('role', e.target.value)}
                      error={!!formErrors.role}
                      required
                    >
                      <MenuItem value="director">Director</MenuItem>
                      <MenuItem value="manager">Manager</MenuItem>
                      <MenuItem value="faculty">Faculty</MenuItem>
                      <MenuItem value="researcher">Researcher</MenuItem>
                      <MenuItem value="staff">Staff</MenuItem>
                      <MenuItem value="student">Student</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                    {formErrors.role && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                        {formErrors.role}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      label="Status"
                      onChange={(e) => handleInputChange('status', e.target.value)}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => handleInputChange('start_date', e.target.value)}
                    error={!!formErrors.start_date}
                    helperText={formErrors.start_date}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Date (Optional)"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => handleInputChange('end_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateOrganization}
              variant="contained"
              disabled={submitLoading}
            >
              {submitLoading ? 'Creating...' : 'Create Organization'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </PageWrapper>
  );
};

export default OrganizationsPage;
